// @erp/ui/theme-toggle — the header's sun/moon switch, and the ONE writer of
// the document's theme.
//
// TWO ACTIVATIONS, ONE SOURCE (decided 2026-09-16, closing audit finding C-01/
// L3/COL-02). daisyUI registers each theme under two selectors —
// `[data-theme=<name>]` and `:root:has(input.theme-controller[value=<name>]
// :checked)` — and this component used to drive only the second: a CSS-only
// checkbox, no attribute, "no runtime swap needed". Every house override that
// is theme-dependent (the `dark:` variant, tokens.css's table-head lift, the
// warning ink split, --shadow-raised, the focus-ring colour) is scoped on the
// ATTRIBUTE form, so in a portal none of it ever fired, and the substrate paid
// a dual-scope tax to paper over the two it noticed. Now the toggle writes
// `document.documentElement.dataset.theme` on mount AND on every change, so
// the attribute form is always true and `[data-theme="shippraDark"]` is the
// only scope a theme-dependent rule needs. The checkbox keeps its
// `theme-controller` class and value: daisyUI's own theme block still hears
// it, and the turnstile widget listens for its `change` event.
//
// FIRST VISIT follows the operating system (`prefers-color-scheme`); a stored
// choice wins over it thereafter. The choice is persisted ONLY when the user
// flips the switch — a first visit that happens to be dark is not written
// down, so a user who never touched the control keeps following their OS.
//
// THE ATTRIBUTE IS THE TRUTH, the checkbox mirrors it: every mounted toggle
// watches `data-theme` on <html> and re-syncs its own `.checked`, so two
// toggles on one page (the catalog's demo gallery mounts seven) cannot drift
// apart — under the old CSS-only design a stale checked rival held daisyUI's
// :has() dark while the newer toggle claimed light.
//
// …AND `onTheme` MIRRORS IT TOO (2026-09-16, the catalog half-theme). The
// mirror used to re-sync only the checkbox, so a consumer that stamped the
// name somewhere else — the catalog wrapper carries `data-theme` beside `dir`
// — heard only ITS toggle's flips. Any other writer (the ThemeToggle demo's
// own control, devtools) moved <html> while that wrapper pinned the old name:
// daisyUI's variables from the wrapper (light), the house
// [data-theme="shippraDark"] rules from <html> (dark), the moon lit in the
// header of a light page. Now the mirror reports the attribute's new value
// through `onTheme`, de-duplicated against the last name reported, so a
// consumer hears each change exactly once whoever wrote it.
import { onCleanup, onMount } from "solid-js";
import { Moon, Sun } from "lucide-solid";

const STORAGE_KEY = "erp-theme";
const LIGHT = "shippraLight";
const DARK = "shippraDark";

/** The product's theme pair — the two names the config package's base.css registers. */
export const PRODUCT_THEMES = { light: LIGHT, dark: DARK } as const;

const readStored = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null; // storage disabled (private mode, blocked site data) — behave as a first visit
  }
};

const prefersDark = (): boolean =>
  typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches;

/**
 * The theme name a page should wear right now: the stored choice when it names
 * one of the pair, else the OS preference (a first visit), else light. A stored
 * name from another pair (a portal that switched palettes) falls back to light
 * rather than to the OS — it is a choice, just not one this pair can honour.
 */
export function resolveTheme(light: string = LIGHT, dark: string = DARK): string {
  const stored = readStored();
  if (stored === dark) return dark;
  if (stored === null) return prefersDark() ? dark : light;
  return light;
}

/**
 * The flip window's marker on `<html>`: while it is present, tokens.css turns
 * every transition off (`[data-theme-flip] *`), so a theme change repaints
 * in ONE frame instead of each surface arriving on its own timer.
 */
export const THEME_FLIP_ATTR = "data-theme-flip";

/**
 * Stamps the theme on `<html>` — the one activation every `[data-theme]` rule
 * keys on — inside a flip window with transitions suppressed.
 *
 * WHY (audit UX-20, decided 2026-09-19): a theme change is a colour change on
 * every surface at once, and the surfaces that carry Spectrum's in-place
 * motion — the text field's 130ms border/colour/background (tokens.css, Adobe
 * textfield:322), the sidebar rows' `transition-all`, every `transition-colors`
 * hover ramp — took that change as a state transition and animated it, while
 * the page ground (no transition) snapped. For a beat after toggling to dark
 * the canvas was black under bright white inputs and unreadable sidebar
 * labels. Dropping those transitions would have thrown away Adobe's own
 * hover/focus motion to fix a defect that is the flip's, not theirs; instead
 * the FLIP is what gets scoped: the marker goes on, the theme is written,
 * and the marker comes off two animation frames later — the first frame
 * commits the new theme with `transition: none` on everything (so no
 * transition starts), the second lifts the suppression once the new values
 * are the before-change style, so nothing is left to animate. Two frames, not
 * a synchronous reflow, because other writers follow `<html>` through a
 * MutationObserver (the catalog wrapper stamps its own `data-theme`) — a
 * microtask, which lands inside the window; a sync removal would have let
 * that second write animate. Without requestAnimationFrame (jsdom without
 * pretendToBeVisual, an old engine) the marker is lifted synchronously.
 */
export function applyTheme(name: string): void {
  const html = document.documentElement;
  html.setAttribute(THEME_FLIP_ATTR, "");
  html.dataset.theme = name;
  // Only the LATEST write lifts the marker: two flips inside one window (a
  // double-click) must not let the first's lift expose the second's repaint.
  const seq = ++flipSeq;
  const lift = () => {
    if (seq === flipSeq) html.removeAttribute(THEME_FLIP_ATTR);
  };
  if (typeof requestAnimationFrame === "function") requestAnimationFrame(() => requestAnimationFrame(lift));
  else lift();
}
let flipSeq = 0;

/** A portal's theme PAIR. Defaults to the product themes. A portal running its
 * own palette passes its two names here — with the pair hardcoded, that
 * portal's toggle would switch it back to the product look instead of flipping
 * its own light/dark. */
export function ThemeToggle(props: {
  id?: string;
  light?: string;
  dark?: string;
  /** Hears the active theme NAME, once per change: on mount (the resolved first
   * paint), on every flip of this toggle, AND whenever another writer — a second
   * toggle, devtools — changes `<html data-theme>`. Re-writes of the same name
   * are not changes. A consumer that stamps the name anywhere else must follow
   * this, not the checkbox: the attribute is the truth. */
  onTheme?: (name: string) => void;
}) {
  let input: HTMLInputElement | undefined;
  const light = () => props.light ?? LIGHT;
  const dark = () => props.dark ?? DARK;

  // The last name handed to `onTheme`. The mirror's callback is a microtask, so
  // by the time it sees this toggle's own write `activate` has already reported
  // that name and the repeat is dropped; an outside write lands as a new name.
  let reported: string | undefined;
  const report = (name: string) => {
    if (name === reported) return;
    reported = name;
    props.onTheme?.(name);
  };

  // Attribute first, then the consumer — a handler that reads <html> sees the new value.
  const activate = (name: string) => {
    applyTheme(name);
    report(name);
  };

  onMount(() => {
    const name = resolveTheme(light(), dark());
    // The `.checked` PROPERTY, not the attribute — daisyUI's :has() and the
    // swap's icon both read the live property.
    if (input) input.checked = name === dark();
    activate(name);

    // Mirror any later writer (another toggle, the catalog, devtools) into the
    // checkbox AND into `onTheme`. A programmatic `.checked` fires no `change`,
    // and `report` drops a repeat, so this cannot loop. An attribute REMOVED
    // (devtools) reads as light — daisyUI's `default: true` theme, and what an
    // unchecked box shows.
    const mirror = new MutationObserver(() => {
      const name = document.documentElement.dataset.theme ?? light();
      if (input) input.checked = name === dark();
      report(name);
    });
    mirror.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    onCleanup(() => mirror.disconnect());
  });

  const onChange = (e: Event & { currentTarget: HTMLInputElement }) => {
    const name = e.currentTarget.checked ? dark() : light();
    try {
      localStorage.setItem(STORAGE_KEY, name);
    } catch {
      /* storage unavailable — the page still switches, it just will not remember */
    }
    activate(name);
  };

  return (
    // State ink: the --ui-state-* ramp (tokens.css) — key-focus == hover, down one step darker (Spectrum table-row tokens).
    // Box: size-(--ds-target), the component-height-100 hit floor (32px, 40 at
    // touch); h-7 w-7 froze it at 28px. Fallback = the old box, for a portal
    // without spectrum.css.
    // has-focus-visible: the checkbox inside is what takes focus, and daisyUI's
    // .swap hides it — so the LABEL wears the key-focus ground (and, through
    // tokens.css `.swap:has(:focus-visible)`, the ring). `focus-visible:` on the
    // label itself never fired: a label is not focusable (audit UX-13, vis=false).
    <label class="swap swap-rotate flex size-[var(--ds-target,1.75rem)] items-center justify-center rounded-300 text-base-subtle transition-colors hover:bg-(--ui-state-hover) has-focus-visible:bg-(--ui-state-hover) active:bg-(--ui-state-down) hover:text-base-content">
      <input
        ref={input}
        id={props.id}
        type="checkbox"
        value={dark()}
        class="theme-controller"
        aria-label="Toggle dark theme"
        onChange={onChange}
      />
      <Sun size={13} class="swap-off" aria-hidden="true" />
      <Moon size={13} class="swap-on" aria-hidden="true" />
    </label>
  );
}
